import React, { useState, useEffect, useCallback } from 'react';
import { Search, MapPin, FileText, User, X, Filter, ChevronLeft, ChevronRight } from 'lucide-react';
import apiService from '../config';
import { useLanguage } from '../StoreContext/LanguageContext';

const SearchRecords = () => {
  const { language, translations } = useLanguage();

  // Search criteria state
  const [projectName, setProjectName] = useState('');
  const [proposalNo, setProposalNo] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [surveyNo, setSurveyNo] = useState('');

  // Results and UI state
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [error, setError] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(50);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Debounced search function
  const performSearch = useCallback(async (page = 1) => {
    const hasSearchCriteria = projectName.trim() || proposalNo.trim() ||
      ownerName.trim() || surveyNo.trim();

    if (!hasSearchCriteria) {
      setResults([]);
      setHasSearched(false);
      setError(null);
      setTotalRecords(0);
      setTotalPages(0);
      return;
    }

    setIsLoading(true);
    setHasSearched(true);
    setError(null);

    try {
      const searchParams = {
        page: page,
        limit: pageSize,
      };

      if (projectName && projectName.trim()) searchParams.projectName = projectName.trim();
      if (proposalNo && proposalNo.trim()) searchParams.proposalNo = proposalNo.trim();
      if (ownerName && ownerName.trim()) searchParams.ownerName = ownerName.trim();
      if (surveyNo && surveyNo.trim()) searchParams.surveyNo = surveyNo.trim();

      console.log('Search Payload:', searchParams);

      const response = await apiService.getAllSearch(searchParams);

      // Set results and pagination data
      setResults(response.data || []);

      if (response.pagination) {
        setCurrentPage(response.pagination.currentPage);
        setTotalRecords(response.pagination.totalRecords);
        setTotalPages(response.pagination.totalPages);
      }

    } catch (err) {
      console.error('Search error:', err);
      setError('Failed to fetch search results. Please try again.');
      setResults([]);
      setTotalRecords(0);
      setTotalPages(0);
    } finally {
      setIsLoading(false);
    }
  }, [projectName, proposalNo, ownerName, surveyNo, pageSize]);

  // Auto-search effect with debouncing
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      performSearch(1); // Reset to page 1 on new search
    }, 500); // 500ms debounce delay

    return () => clearTimeout(debounceTimer);
  }, [projectName, proposalNo, ownerName, surveyNo]);

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      performSearch(newPage);
      // Scroll to top of results
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const resetSearch = () => {
    setProjectName('');
    setProposalNo('');
    setOwnerName('');
    setSurveyNo('');
    setResults([]);
    setHasSearched(false);
    setError(null);
    setCurrentPage(1);
    setTotalRecords(0);
    setTotalPages(0);
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (projectName.trim()) count++;
    if (proposalNo.trim()) count++;
    if (ownerName.trim()) count++;
    if (surveyNo.trim()) count++;
    return count;
  };

  // Pagination component
  const Pagination = () => {
    if (totalPages <= 1) return null;

    const getPageNumbers = () => {
      const pages = [];
      const maxVisible = 5;

      if (totalPages <= maxVisible) {
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        if (currentPage <= 3) {
          for (let i = 1; i <= 4; i++) pages.push(i);
          pages.push('...');
          pages.push(totalPages);
        } else if (currentPage >= totalPages - 2) {
          pages.push(1);
          pages.push('...');
          for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
        } else {
          pages.push(1);
          pages.push('...');
          for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
          pages.push('...');
          pages.push(totalPages);
        }
      }

      return pages;
    };

    return (
      <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <span className="font-medium">
            Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalRecords)} of {totalRecords} results
          </span>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="flex items-center gap-1 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Previous
          </button>

          <div className="flex gap-1">
            {getPageNumbers().map((page, index) => (
              page === '...' ? (
                <span key={`ellipsis-${index}`} className="px-3 py-2 text-gray-500">...</span>
              ) : (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`px-3 py-2 rounded-lg transition-colors ${currentPage === page
                    ? 'bg-blue-500 text-white font-medium'
                    : 'hover:bg-gray-100 text-gray-700'
                    }`}
                >
                  {page}
                </button>
              )
            ))}
          </div>

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="flex items-center gap-1 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Next
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-black mb-3">
            All Records Search
          </h1>
          <p className="text-gray-600 text-lg">
            Search across all projects and proposals
          </p>
        </div>

        {/* Enhanced Search Section */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden mb-6">
          {/* Search Header */}
          <div className="bg-gradient-to-r from-blue-500 to-indigo-600 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Search className="w-6 h-6 text-white" />
                <h2 className="text-xl font-semibold text-white">Search Filters</h2>
                {getActiveFiltersCount() > 0 && (
                  <span className="bg-white text-blue-600 px-3 py-1 rounded-full text-sm font-medium">
                    {getActiveFiltersCount()} Active
                  </span>
                )}
              </div>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="lg:hidden flex items-center gap-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg text-white transition-colors"
              >
                <Filter className="w-4 h-4" />
                Filters
              </button>
            </div>
          </div>

          {/* Search Fields */}
          <div className={`p-6 ${showFilters ? 'block' : 'hidden lg:block'}`}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Project Name */}
              <div className="relative">
                <label className="flex items-center text-sm font-medium text-gray-700 mb-3">
                  <FileText className="w-4 h-4 mr-2 text-blue-500" />
                  Project Name
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={projectName}
                    onChange={(e) => setProjectName(e.target.value)}
                    placeholder="Enter project name"
                    className="w-full px-4 py-3 pl-4 pr-10 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 placeholder-gray-400 bg-gray-50 focus:bg-white transition-colors"
                  />
                  {projectName && (
                    <button
                      onClick={() => setProjectName('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Proposal No */}
              <div className="relative">
                <label className="flex items-center text-sm font-medium text-gray-700 mb-3">
                  <FileText className="w-4 h-4 mr-2 text-purple-500" />
                  Proposal No
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={proposalNo}
                    onChange={(e) => setProposalNo(e.target.value)}
                    placeholder="Enter proposal number"
                    className="w-full px-4 py-3 pl-4 pr-10 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 placeholder-gray-400 bg-gray-50 focus:bg-white transition-colors"
                  />
                  {proposalNo && (
                    <button
                      onClick={() => setProposalNo('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Owner Name */}
              <div className="relative">
                <label className="flex items-center text-sm font-medium text-gray-700 mb-3">
                  <User className="w-4 h-4 mr-2 text-green-500" />
                  Owner Name
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={ownerName}
                    onChange={(e) => setOwnerName(e.target.value)}
                    placeholder="Enter owner name"
                    className="w-full px-4 py-3 pl-4 pr-10 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 placeholder-gray-400 bg-gray-50 focus:bg-white transition-colors"
                  />
                  {ownerName && (
                    <button
                      onClick={() => setOwnerName('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Survey No */}
              <div className="relative">
                <label className="flex items-center text-sm font-medium text-gray-700 mb-3">
                  <FileText className="w-4 h-4 mr-2 text-orange-500" />
                  Survey No
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={surveyNo}
                    onChange={(e) => setSurveyNo(e.target.value)}
                    placeholder="Enter survey number"
                    className="w-full px-4 py-3 pl-4 pr-10 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 placeholder-gray-400 bg-gray-50 focus:bg-white transition-colors"
                  />
                  {surveyNo && (
                    <button
                      onClick={() => setSurveyNo('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4 items-center justify-between mt-6 pt-6 border-t border-gray-100">
              <div className="flex gap-3">
                <button
                  onClick={resetSearch}
                  className="flex items-center gap-2 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-colors"
                >
                  <X className="w-4 h-4" />
                  Clear All
                </button>
              </div>

              {isLoading && (
                <div className="flex items-center gap-3 text-blue-600">
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-600 border-t-transparent"></div>
                  <span className="text-sm font-medium">Searching records...</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-6 mb-6">
            <div className="flex items-center gap-3">
              <X className="w-5 h-5 text-red-500" />
              <div>
                <h3 className="text-red-800 font-medium">Search Error</h3>
                <p className="text-red-700 text-sm mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Results Section */}
        {results.length > 0 && (
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            <div className="bg-gradient-to-r from-emerald-500 to-teal-600 px-6 py-4">
              <h2 className="text-xl font-semibold text-white">
                Search Results ({totalRecords} Total)
              </h2>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Project Name
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Proposal No
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Survey No
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Owner Name
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Location
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {results.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">
                        {item.project_name || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-lg text-xs font-medium">
                          {item.acquisition_proposal_no || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-lg text-xs font-medium">
                          {item.survey_no || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">
                        {item.owner_name || 'N/A'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <div className="flex items-center">
                          <MapPin className="w-4 h-4 mr-2 text-gray-400" />
                          <div className="flex flex-col">
                            <span className="font-medium">
                              {item.village_name || 'N/A'}
                            </span>
                            <span className="text-xs text-gray-500">
                              {item.taluka_name || ''}{item.taluka_name && item.dist_name ? ', ' : ''}{item.dist_name || ''}
                            </span>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <Pagination />
          </div>
        )}

        {/* No Results */}
        {hasSearched && results.length === 0 && !isLoading && !error && (
          <div className="text-center py-16 bg-white rounded-2xl shadow-xl border border-gray-100">
            <div className="text-gray-300 mb-6">
              <Search className="w-16 h-16 mx-auto" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">No Records Found</h3>
            <p className="text-gray-600 mb-6">Try adjusting your search criteria</p>
            <button
              onClick={resetSearch}
              className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-xl font-medium transition-colors"
            >
              Clear Search
            </button>
          </div>
        )}

        {/* Initial State */}
        {!hasSearched && results.length === 0 && !isLoading && (
          <div className="text-center py-16 bg-white rounded-2xl shadow-xl border border-gray-100">
            <div className="text-gray-300 mb-6">
              <Search className="w-16 h-16 mx-auto" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Ready to Search</h3>
            <p className="text-gray-600 mb-6">Enter search criteria to find records</p>
            <div className="flex flex-wrap justify-center gap-2 text-sm text-gray-500">
              <span className="bg-gray-100 px-3 py-1 rounded-lg">Project Name</span>
              <span className="bg-gray-100 px-3 py-1 rounded-lg">Proposal No</span>
              <span className="bg-gray-100 px-3 py-1 rounded-lg">Owner Name</span>
              <span className="bg-gray-100 px-3 py-1 rounded-lg">Survey No</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchRecords;